import {useState, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, FormGroup, Checkbox, Typography, Tooltip} from '@mui/material'
import {styled} from '@mui/material/styles'
import FilterHeader from '../FilterHeader/FilterHeader'
import RadioGroup from '../RadioGroup'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'

/**
 * Styled components for OutreachFilterControl
 */
const OptionsContainer = styled(Box)(({theme}) => ({
  paddingLeft: theme.spacing(1),
  paddingTop: theme.spacing(1)
}))

const StyledFormControlLabel = styled('label', {
  shouldForwardProp: (prop) => prop !== 'isDisabled'
})(({theme, isDisabled}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  fontSize: theme.typography.subtitle1.fontSize,
  color: isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
  opacity: isDisabled ? 0.6 : 1
}))

const EnrolledSection = styled(Box)(({theme}) => ({
  marginTop: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`
}))

const EnrolledLabel = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5)
}))

/**
 * OutreachFilterControl - Filter control for Benefits Eligibility / Workforce Recruitment
 *
 * A specialized filter control that combines:
 * - Checkbox sub-options (Select All, SNAP, Medicare, etc.)
 * - Enrolled status radio buttons (All/Yes/No)
 *
 * Value structure: { selected: ['SNAP', 'Medicare'], enrolledValue: 2 }
 * where enrolledValue: 0=No, 1=Yes, 2=All
 *
 * Query output format: benefits: [{snapEnrolled: [0]}, {medicareEnrolled: [0,1]}]
 */
const OutreachFilterControl = ({
  label = 'Filter',
  description,
  options = [],
  enrolledLabel = 'Enrolled',
  value = null,
  onChange,
  onRemove,
  showRemoveButton = true,
  categoryTitle = 'Outreach Simulation', // Category title for analytics
  ...rest
}) => {
  // Default value structure
  const defaultValue = {selected: [], enrolledValue: 2}

  // Local state
  const [localValue, setLocalValue] = useState(value || defaultValue)

  // Update local state when external value changes
  useEffect(() => {
    setLocalValue(value || defaultValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Enrolled status options
  const enrolledOptions = [
    {label: 'All', value: 2},
    {label: 'Yes', value: 1},
    {label: 'No', value: 0}
  ]

  // Check if any options are selected
  const hasSelectedOptions = localValue.selected && localValue.selected.length > 0

  // Performance optimization: Create a Set for O(1) lookup
  const selectedSet = useMemo(() => new Set(localValue.selected || []), [localValue.selected])

  // Get enabled options (not disabled)
  const enabledOptions = useMemo(() => options.filter((opt) => !opt.disabled), [options])

  // Check if all enabled options are selected (for Select All state)
  const allSelected = useMemo(() => {
    if (enabledOptions.length === 0) return false
    return enabledOptions.every((opt) => selectedSet.has(opt.value))
  }, [enabledOptions, selectedSet])

  const someSelected = useMemo(() => {
    if (enabledOptions.length === 0) return false
    const selectedCount = enabledOptions.filter((opt) => selectedSet.has(opt.value)).length
    return selectedCount > 0 && selectedCount < enabledOptions.length
  }, [enabledOptions, selectedSet])

  /**
   * Handle individual checkbox change
   */
  const handleCheckboxChange = (optionValue, optionLabel) => {
    let newSelected
    const isAdding = !selectedSet.has(optionValue)

    if (selectedSet.has(optionValue)) {
      // Remove from selection
      newSelected = localValue.selected.filter((v) => v !== optionValue)
    } else {
      // Add to selection
      newSelected = [...(localValue.selected || []), optionValue]
    }

    const newValue = {
      selected: newSelected,
      enrolledValue: localValue.enrolledValue
    }

    setLocalValue(newValue)
    if (onChange) {
      onChange(newValue)
    }

    // Send analytics when adding an option (e.g., clicking SNAP)
    if (isAdding) {
      AnalyticsHandler.onFilterApply({
        name: optionLabel || optionValue,
        category: categoryTitle,
        subCategory: label
      })
    }
  }

  /**
   * Handle Select All checkbox
   * Only selects enabled options (not disabled ones)
   */
  const handleSelectAll = (event) => {
    let newSelected
    if (event.target.checked) {
      // Only select enabled options
      newSelected = enabledOptions.map((opt) => opt.value)
    } else {
      // Deselect all enabled options, keep disabled ones that were somehow selected
      const disabledSelectedValues = (localValue.selected || []).filter((val) =>
        options.find((opt) => opt.value === val && opt.disabled)
      )
      newSelected = disabledSelectedValues
    }

    const newValue = {
      selected: newSelected,
      enrolledValue: localValue.enrolledValue
    }

    setLocalValue(newValue)
    if (onChange) {
      onChange(newValue)
    }
  }

  /**
   * Handle enrolled status radio change
   */
  const handleEnrolledChange = (event, newEnrolledValue) => {
    // Radio values come as strings, convert to number
    const parsedValue = typeof newEnrolledValue === 'string' ? Number.parseInt(newEnrolledValue, 10) : newEnrolledValue

    const newValue = {
      selected: localValue.selected || [],
      enrolledValue: parsedValue
    }

    setLocalValue(newValue)
    if (onChange) {
      onChange(newValue)
    }
  }

  return (
    <Box width='100%' {...rest}>
      <FilterHeader label={label} description={description} onRemove={onRemove} showRemoveButton={showRemoveButton} />

      <OptionsContainer>
        <FormGroup>
          {/* Select All Option - Only show when more than one enabled option */}
          {enabledOptions.length > 1 && (
            <StyledFormControlLabel>
              <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} size='small' />
              Select All
            </StyledFormControlLabel>
          )}

          {/* Individual Options */}
          {options.map((option) => {
            const checkboxElement = (
              <StyledFormControlLabel key={option.value} isDisabled={option.disabled}>
                <Checkbox
                  checked={selectedSet.has(option.value)}
                  onChange={() => handleCheckboxChange(option.value, option.label)}
                  size='small'
                  disabled={option.disabled}
                />
                {option.label}
              </StyledFormControlLabel>
            )

            // Wrap with tooltip if disabled and has tooltip text
            if (option.disabled && option.tooltipText) {
              return (
                <Tooltip key={option.value} title={option.tooltipText} placement='top' arrow>
                  <Box component='div' sx={{display: 'inline-block'}}>
                    {checkboxElement}
                  </Box>
                </Tooltip>
              )
            }

            return checkboxElement
          })}
        </FormGroup>

        {/* Enrolled Status - Only show when options are selected */}
        {hasSelectedOptions && (
          <EnrolledSection>
            <EnrolledLabel>{enrolledLabel}</EnrolledLabel>
            <RadioGroup
              options={enrolledOptions}
              value={localValue.enrolledValue}
              onChange={handleEnrolledChange}
              orientation='horizontal'
              ariaLabel={enrolledLabel}
              name={`enrolled-${label.toLowerCase().replaceAll(/\s+/g, '-')}`}
            />
          </EnrolledSection>
        )}
      </OptionsContainer>
    </Box>
  )
}

OutreachFilterControl.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      enrolledKey: PropTypes.string,
      disabled: PropTypes.bool,
      tooltipText: PropTypes.string // Shown when option is disabled
    })
  ).isRequired,
  enrolledLabel: PropTypes.string,
  value: PropTypes.shape({
    selected: PropTypes.arrayOf(PropTypes.string),
    enrolledValue: PropTypes.number
  }),
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  categoryTitle: PropTypes.string
}

export default OutreachFilterControl
